import { Button, chakra, HStack, Text } from '@chakra-ui/react'
import { faMessage, faWaveformLines } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { DateTime, Duration } from 'luxon'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { millisecondsToHumanReadableString } from '@/utils/time'

import * as url from '@/client/services/url'
import { getSeriesUrl } from '@/client/services/url'

import ShwLink from '@/components/ShwLink'

import { NO_SHOW_THRESHOLD_MINUTES, ZOOM_OPEN_THRESHOLD_MINUTES } from './constants'
import { type BannerOptions, BannerStateType } from './types'

const UPCOMING_SOON_OPTIONS = ({
  startFromNow,
  isUserTutor,
}: {
  startFromNow: Duration
  isUserTutor: boolean
}) => ({
  colors: {
    accentColor: isUserTutor ? 'secondary.solid' : 'primary.solid',
    borderIconColor: isUserTutor ? 'bg.subtle' : 'primary.subtle',
    primaryTextColor: isUserTutor ? 'secondary.contrast' : 'primary.contrast',
    secondaryTextColor: isUserTutor ? 'secondary.contrast' : 'primary.contrast',
    shadowColor: isUserTutor ? 'secondary.fg' : 'primary.fg',
    shapeColor: isUserTutor ? 'secondary.fg' : 'primary.fg',
    backgroundColor: isUserTutor ? 'secondary.solid' : 'primary.solid',
  },
  aboveAlert: null,
  alert: null,
  beneathAlert: null,
  showVideoIcon: true,
  nextToTitleTag: {
    bgColor: 'yellow.solid',
    textColor: 'fg',
    text: `STARTING IN ${startFromNow.toFormat('mm:ss')}`,
    icon: null,
    width: '118px',
  },
  showTimespanUnderneathTitle: false,
  button: {
    disabledWithTooltip: true,
  },
  showCornerCountdownTag: false,
})

const LIVE_OPTIONS = ({
  startFromNow,
  isUserTutor,
}: {
  startFromNow: Duration
  isUserTutor: boolean
}) => {
  const upcomingSoonOptions = UPCOMING_SOON_OPTIONS({ startFromNow, isUserTutor })

  return {
    ...upcomingSoonOptions,
    nextToTitleTag: {
      ...upcomingSoonOptions.nextToTitleTag,
      text: 'LIVE NOW',
      icon: <FontAwesomeIcon icon={faWaveformLines} color={chakraTokenToVar('warning.fg')} />,
      width: null,
    },
    button: {
      disabled: false,
      tooltip: null,
    },
  }
}

export const BANNER_STATE_TYPE_TO_OPTIONS: Record<
  BannerStateType,
  ({
    startFromNow,
    isUserTutor,
    startDate,
    seriesId,
    hasCompletedSimulation,
    showSimulatorNudge,
    now,
  }: {
    startFromNow: Duration
    isUserTutor: boolean
    startDate: DateTime
    seriesId: number
    hasCompletedSimulation?: boolean
    showSimulatorNudge?: boolean
    now: DateTime
  }) => BannerOptions
> = {
  [BannerStateType.UpcomingDistant]: ({ isUserTutor, startFromNow, showSimulatorNudge }) => ({
    colors: {
      accentColor: isUserTutor ? 'secondary.solid' : 'primary.solid',
      borderIconColor: isUserTutor ? 'secondary.solid' : 'primary.solid',
      primaryTextColor: 'fg',
      secondaryTextColor: 'fg.muted',
      shadowColor: isUserTutor ? 'secondary.fg' : 'primary.fg',
      shapeColor: isUserTutor ? 'secondary.subtle' : 'primary.subtle',
      backgroundColor: 'bg',
    },
    aboveAlert: null,
    alert: (() => {
      if (showSimulatorNudge !== true || startFromNow.as('hours') < 2) {
        return null
      }

      return {
        variant: 'info',
        message: (
          <HStack spacing="8px" alignItems="center" justifyContent="space-between">
            <Text flex="1">
              Spend a few minutes practicing before your session so you feel confident and ready to
              help learners.
            </Text>
            <ShwLink href={`${url.getSessionSimulatorUrl()}?src=series-banner`}>
              <Button
                size="sm"
                variant="outline"
                borderColor="info.border"
                color="info.fg"
                _hover={{ bg: 'info.subtle' }}
              >
                Start Practice
              </Button>
            </ShwLink>
          </HStack>
        ),
      }
    })(),
    beneathAlert: null,
    showVideoIcon: false,
    nextToTitleTag: null,
    showTimespanUnderneathTitle: true,
    button: null,
    showCornerCountdownTag: true,
  }),
  [BannerStateType.UpcomingSoonZoomClosed]: ({ startFromNow, isUserTutor }) => ({
    ...UPCOMING_SOON_OPTIONS({ startFromNow, isUserTutor }),
    button: {
      disabled: true,
      tooltip: `The Zoom room will open ${ZOOM_OPEN_THRESHOLD_MINUTES} minutes before the start of this session. Hang tight!`,
    },
  }),
  [BannerStateType.UpcomingSoonZoomOpen]: ({ startFromNow, isUserTutor }) => ({
    ...UPCOMING_SOON_OPTIONS({ startFromNow, isUserTutor }),
    button: {
      disabled: false,
      tooltip: null,
    },
  }),
  [BannerStateType.Live]: ({ startFromNow, isUserTutor }) =>
    LIVE_OPTIONS({ startFromNow, isUserTutor }),
  [BannerStateType.LiveNoShowWarning]: ({ startFromNow, isUserTutor, startDate, now }) => {
    const diff = millisecondsToHumanReadableString(
      startDate
        .plus({ minutes: -1 * NO_SHOW_THRESHOLD_MINUTES })
        .diff(now)
        .as('milliseconds')
    )
    return {
      ...LIVE_OPTIONS({ startFromNow, isUserTutor }),
      alert: {
        variant: 'warning',
        message: isUserTutor
          ? `Remember to start your session in ${diff}, otherwise you will be marked as a no-show.`
          : `If your tutor does not show in ${diff}, we will mark them as a no-show and this session will be cancelled.`,
      },
    }
  },
  [BannerStateType.LiveSessionEndedEarly]: ({ startFromNow, isUserTutor }) => ({
    ...LIVE_OPTIONS({ startFromNow, isUserTutor }),
    alert: !isUserTutor
      ? {
          variant: 'warning',
          message:
            'Your tutor may have ended the session early. You may try to join, but they might be unavailable.',
        }
      : null,
  }),
  [BannerStateType.NoShow]: ({ isUserTutor, seriesId }) => ({
    colors: {
      accentColor: 'error.solid',
      borderIconColor: 'error.subtle',
      primaryTextColor: 'error.contrast',
      secondaryTextColor: 'error.contrast',
      shadowColor: 'error.fg',
      shapeColor: 'error.fg',
      backgroundColor: 'error.solid',
    },
    aboveAlert: isUserTutor ? (
      <Text fontWeight="semibold" color="error.contrast">
        If 15 minutes pass without a tutor starting a session, we automatically end it and notify
        the learners. We do this so that learners have a consistent experience on our platform.
      </Text>
    ) : null,
    alert: {
      variant: 'error',
      message: isUserTutor ? (
        <Text fontWeight="semibold">
          <chakra.span color="error.fg">ACTION REQUIRED:</chakra.span> As soon as possible, please
          notify your session group chat letting them know why you couldn’t make the session, and
          how they can make up the missed content.
        </Text>
      ) : (
        'Your tutor missed the session. We’re very sorry for the inconvenience. Though this doesn’t happen often, when it does, we know it has a big impact on our learners. Rest assured, we have contacted the tutor to ensure this does not happen again.'
      ),
    },
    beneathAlert: isUserTutor ? (
      <ShwLink href={getSeriesUrl(seriesId, { path: 'messages' })}>
        <Button
          width="100%"
          variant="unstyled"
          px="24px"
          minW="unset"
          border={`2px solid ${chakraTokenToVar('border')}`}
          bg="bg"
          display="flex"
          alignItems="center"
          leftIcon={<FontAwesomeIcon icon={faMessage} size="xs" />}
        >
          Notify Learners
        </Button>
      </ShwLink>
    ) : null,
    showVideoIcon: true,
    nextToTitleTag: {
      bgColor: 'error.fg',
      textColor: 'error.subtle',
      icon: null,
      text: 'CANCELLED',
      width: null,
    },
    showTimespanUnderneathTitle: false,
    button: null,
    showCornerCountdownTag: false,
  }),
  [BannerStateType.None]: () => {
    throw new Error(
      'No banner should be shown if the banner state type is None. This code should be unreachable.'
    )
  },
}
